// packages/chatbot/src/components/TabsBar.tsx

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import type { ConversationSummary } from '../types';
import { X } from 'lucide-react';
import { NEW_CHAT_ID } from '../utils/constants';

interface TabsBarProps {
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  onSelectConversation: (id: string | null) => void;
  // New: a sessionStorage key to persist order (e.g., dopo:chat:tabOrder:agent:{id} or :site)
  orderKey?: string;
  // Optional callback for parent awareness
  onOrderChange?: (ids: string[]) => void;
}

function displayTitle(c: ConversationSummary) {
  return c.first_user_message_preview || 'New Chat';
}

export const TabsBar: React.FC<TabsBarProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  orderKey,
  onOrderChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  // Persist and restore order
  const loadOrder = useCallback((): string[] => {
    if (!orderKey) return conversations.map(c => c.id);
    try {
      const raw = sessionStorage.getItem(orderKey);
      const saved: string[] | null = raw ? JSON.parse(raw) : null;
      const idsNow = conversations.map(c => c.id);
      if (!saved || !Array.isArray(saved)) return idsNow;
      // Keep only existing IDs, keep saved order, append new ones
      const filtered = saved.filter(id => idsNow.includes(id));
      const missing = idsNow.filter(id => !filtered.includes(id));
      return [...filtered, ...missing];
    } catch {
      return conversations.map(c => c.id);
    }
  }, [orderKey, conversations]);

  const [orderedIds, setOrderedIds] = useState<string[]>(loadOrder);

  // Keep order in sync with incoming conversation changes
  useEffect(() => {
    const next = loadOrder();
    setOrderedIds(prev => {
      const same = JSON.stringify(prev) === JSON.stringify(next);
      return same ? prev : next;
    });
  }, [loadOrder]);

  // Save order when it changes
  useEffect(() => {
    if (orderKey) {
      try { sessionStorage.setItem(orderKey, JSON.stringify(orderedIds)); } catch {}
    }
    onOrderChange?.(orderedIds);
  }, [orderedIds, orderKey, onOrderChange]);

  const tabs = useMemo(() => {
    const byId = new Map(conversations.map(c => [c.id, c]));
    return orderedIds.map(id => byId.get(id)).filter(Boolean) as ConversationSummary[];
  }, [conversations, orderedIds]);

  const activeIndex = useMemo(() => {
    const idx = tabs.findIndex(t => t.id === activeConversationId);
    if (idx >= 0) return idx;
    const newIdx = tabs.findIndex(t => t.id === NEW_CHAT_ID);
    return newIdx >= 0 ? newIdx : 0;
  }, [tabs, activeConversationId]);

  // Keyboard navigation: Left/Right to switch tabs; Ctrl/Cmd+W to start new chat
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => {
      if (!el.contains(document.activeElement)) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        if (!tabs.length) return;
        let next = activeIndex + (e.key === 'ArrowRight' ? 1 : -1);
        if (next < 0) next = tabs.length - 1;
        if (next > tabs.length - 1) next = 0;
        const nextId = tabs[next]?.id;
        if (nextId) onSelectConversation(nextId === NEW_CHAT_ID ? null : nextId);
      } else if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'w')) {
        e.preventDefault();
        // We don't delete history; we start a new chat instead.
        onSelectConversation(null);
      }
    };
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
  }, [tabs, activeIndex, onSelectConversation]);

  // Drag helpers
  const onDragStart = (e: React.DragEvent, id: string) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // allow drop
    e.dataTransfer.dropEffect = 'move';
  };
  const onDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const srcId = draggingId || e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    if (!srcId || srcId === targetId) return;
    setOrderedIds(prev => {
      const srcIndex = prev.indexOf(srcId);
      const tgtIndex = prev.indexOf(targetId);
      if (srcIndex === -1 || tgtIndex === -1) return prev;
      const next = [...prev];
      next.splice(srcIndex, 1);
      next.splice(tgtIndex, 0, srcId);
      return next;
    });
  };

  const handleCloseTab = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    // Do not show close for the placeholder, but guard anyway
    if (id === NEW_CHAT_ID) return;
    setOrderedIds(prev => prev.filter(x => x !== id));
    if (id === activeConversationId) {
      // We don't delete; we just start a new chat
      onSelectConversation(null);
    }
  };

  return (
    <div
      ref={containerRef}
      className="flex items-center gap-1 px-2 py-2 border-b border-slate-200 overflow-x-auto"
      role="tablist"
      aria-label="Conversations"
      tabIndex={0}
    >
      {tabs.map((c) => {
        const isActive = c.id === activeConversationId || (c.id === NEW_CHAT_ID && !activeConversationId);
        return (
          <button
            key={c.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelectConversation(c.id === NEW_CHAT_ID ? null : c.id)}
            draggable={c.id !== NEW_CHAT_ID}
            onDragStart={(e) => onDragStart(e, c.id)}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(e, c.id)}
            className={clsx(
              'px-3 py-1.5 border-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all',
              isActive
                ? 'bg-white border-black shadow-[2px_2px_0_0_#000] text-slate-900'
                : 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
            )}
            title={displayTitle(c)}
          >
            <span className="inline-flex items-center gap-2">
              <span className="truncate max-w-[16ch]" title={displayTitle(c)}>{displayTitle(c)}</span>
              {c.id !== NEW_CHAT_ID && (
                <X
                  className="w-3.5 h-3.5 text-slate-500 hover:text-slate-700"
                  onClick={(e) => handleCloseTab(e, c.id)}
                />
              )}
            </span>
          </button>
        );
      })}

      <button
        onClick={() => onSelectConversation(null)}
        className="ml-1 px-2 py-1.5 border-2 border-black rounded-lg text-sm font-bold bg-emerald-400 text-slate-900 hover:bg-emerald-300 transition-colors"
        aria-label="Start new chat"
        title="New chat"
      >
        +
      </button>
    </div>
  );
};

export default TabsBar;
